import { Asset } from '@/api/generatedApi';
import { RenderCreator, RenderFolder } from '@/components/ContentsTable/cellDefinitions';
import { CopiableGridColDef } from '@/types/copiableColumn';
import { getAssetFolderDirectory, getAssetName } from '@/utils/assets';
import { getCreatorEmailValue } from '@/utils/dataGridValueGetters';
import { dateFormat } from '@/utils/format';
import { getFileExtension } from '@physna/supported-files';
import { MatchAsset } from '.';
import { SearchType } from '../types';
import {
  RenderCreatedAt,
  RenderDetails,
  RenderForwardMatchPercentage,
  RenderMatchPercentage,
  RenderName,
  RenderReverseMatchPercentage,
} from './cellDefinitions';
import { getExtensionFilterOperators, getFolderFilterOperators, MetadataFilterOption } from './filterOperators';

const getBaseColumns = (
  canOnlySearch: boolean,
  isCrossTenant: boolean,
  searchType?: SearchType,
  extensionOptions?: MetadataFilterOption[],
  folderOptions?: MetadataFilterOption[],
  onApplyFilter?: () => void
): CopiableGridColDef<MatchAsset>[] => [
  {
    field: 'name',
    headerName: 'Name',
    width: 300,
    minWidth: 270,
    filterable: false,
    sortable: false,
    flex: 2,
    renderCell: RenderName,
    getCopyValue: row => getAssetName(row),
  },
  {
    field: 'folder',
    headerName: 'Folder',
    width: 200,
    filterable: true,
    filterOperators: getFolderFilterOperators(folderOptions, onApplyFilter),
    sortable: false,
    renderCell: RenderFolder,
    valueGetter: (_value, row) => row.path?.split('/').slice(0, -1).join('/') ?? '',
    getCopyValue: row => getAssetFolderDirectory(row) || undefined,
  },
  {
    field: 'extension',
    headerName: 'Extension',
    width: 100,
    filterable: true,
    filterOperators: getExtensionFilterOperators(extensionOptions, onApplyFilter),
    sortable: false,
    valueGetter: (_value, row) => getFileExtension(row.path ?? '').toLowerCase(),
    getCopyValue: row => getFileExtension(row.path ?? '').toLowerCase() || undefined,
  },
  {
    field: 'details',
    headerName: 'Details',
    width: 390,
    minWidth: 170,
    filterable: false,
    sortable: false,
    flex: 1,
    renderCell: parameters => RenderDetails(parameters, canOnlySearch, isCrossTenant, searchType),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 100,
    filterable: false,
    sortable: false,
    renderCell: RenderCreatedAt,
    type: 'date',
    valueGetter: (value: string | Date | undefined) => (value ? new Date(value) : undefined),
    getCopyValue: row => (row.createdAt ? dateFormat(row.createdAt) : undefined),
  },
  {
    field: 'creator',
    headerName: 'Created By',
    width: 130,
    filterable: false,
    sortable: false,
    renderCell: RenderCreator,
    valueGetter: getCreatorEmailValue,
    getCopyValue: row => row.creator?.email,
  },
];

const formatPercentage = (value?: number): string | undefined => {
  if (value === undefined) return undefined;
  return `${Math.round(value)}%`;
};

const getGeometricMatchColumns = (searchAsset?: Asset): CopiableGridColDef<MatchAsset>[] => [
  {
    field: 'matchPercentage',
    headerName: 'Match %',
    width: 98,
    renderCell: parameters => RenderMatchPercentage(parameters, searchAsset),
    sortable: false,
    filterable: false,
    getCopyValue: row => formatPercentage(row.matchPercentage),
  },
];

const getPartMatchColumns = (searchAsset?: Asset): CopiableGridColDef<MatchAsset>[] => [
  {
    field: 'forwardMatchPercentage',
    headerName: '% in Match',
    width: 98,
    renderCell: parameters => RenderForwardMatchPercentage(parameters, searchAsset),
    sortable: false,
    filterable: false,
    getCopyValue: row => formatPercentage(row.forwardMatchPercentage),
  },
  {
    field: 'reverseMatchPercentage',
    headerName: '% of Match',
    width: 98,
    renderCell: parameters => RenderReverseMatchPercentage(parameters, searchAsset),
    sortable: false,
    filterable: false,
    getCopyValue: row => formatPercentage(row.reverseMatchPercentage),
  },
];

export const getColumns = ({
  searchType,
  canOnlySearch,
  searchAsset,
  isCrossTenant,
  extensionOptions,
  folderOptions,
  onApplyFilter,
}: {
  searchType: SearchType;
  canOnlySearch: boolean;
  searchAsset?: Asset;
  isCrossTenant: boolean;
  extensionOptions?: MetadataFilterOption[];
  folderOptions?: MetadataFilterOption[];
  onApplyFilter?: () => void;
}): CopiableGridColDef<MatchAsset>[] => {
  const baseColumns = getBaseColumns(
    canOnlySearch,
    isCrossTenant,
    searchType,
    extensionOptions,
    folderOptions,
    onApplyFilter
  );

  if (searchType === 'geometric') {
    return [...getGeometricMatchColumns(searchAsset), ...baseColumns];
  } else if (searchType === 'part') {
    return [...getPartMatchColumns(searchAsset), ...baseColumns];
  }

  return baseColumns;
};
